// Math symbol mappings for autocomplete
import { db } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface MathSymbol {
    trigger: string[];
    unicode: string;
    latex: string;
    description: string;
    category: string;
    id?: string;
    isCustom?: boolean;
    isPopular?: boolean;
    skipAutoConvert?: boolean; // if true, don't auto-wrap in $...$ during view render — Unicode already looks correct
}

export interface MathPattern {
    pattern: string;
    latex: string;
    description: string;
    priority?: number;
    id?: string;
    isCustom?: boolean;
    isActive?: boolean;
    mathZoneOnly?: boolean; // if true, only applies inside $$...$$ blocks
    example?: string;       // short sentence showing the pattern used in context
}

// Default symbols - organized by category
const DEFAULT_SYMBOLS: MathSymbol[] = [
    // ==================================================================================
    // 1. GREEK LETTERS
    // ==================================================================================
    // Lowercase
    { trigger: ['alpha'], unicode: 'α', latex: '\\alpha', description: 'Alpha', category: 'Greek', isPopular: true },
    { trigger: ['beta'], unicode: 'β', latex: '\\beta', description: 'Beta', category: 'Greek', isPopular: true },
    { trigger: ['gamma'], unicode: 'γ', latex: '\\gamma', description: 'Gamma', category: 'Greek', isPopular: true },
    { trigger: ['delta', 'deta'], unicode: 'δ', latex: '\\delta', description: 'Delta', category: 'Greek', isPopular: true },
    { trigger: ['epsilon', 'eps'], unicode: 'ε', latex: '\\varepsilon', description: 'Epsilon', category: 'Greek', isPopular: true },
    { trigger: ['zeta'], unicode: 'ζ', latex: '\\zeta', description: 'Zeta', category: 'Greek' },
    { trigger: ['eta'], unicode: 'η', latex: '\\eta', description: 'Eta', category: 'Greek' },
    { trigger: ['theta'], unicode: 'θ', latex: '\\theta', description: 'Theta', category: 'Greek', isPopular: true },
    { trigger: ['iota'], unicode: 'ι', latex: '\\iota', description: 'Iota', category: 'Greek' },
    { trigger: ['kappa'], unicode: 'κ', latex: '\\kappa', description: 'Kappa', category: 'Greek' },
    { trigger: ['lambda', 'lbd'], unicode: 'λ', latex: '\\lambda', description: 'Lambda', category: 'Greek', isPopular: true },
    { trigger: ['mu'], unicode: 'μ', latex: '\\mu', description: 'Mu', category: 'Greek', isPopular: true },
    { trigger: ['nu'], unicode: 'ν', latex: '\\nu', description: 'Nu', category: 'Greek' },
    { trigger: ['xi', 'xi_char'], unicode: 'ξ', latex: '\\xi', description: 'Xi', category: 'Greek' },
    { trigger: ['pi', 'pi_char'], unicode: 'π', latex: '\\pi', description: 'Pi', category: 'Greek', isPopular: true },
    { trigger: ['rho'], unicode: 'ρ', latex: '\\rho', description: 'Rho', category: 'Greek' },
    { trigger: ['sigma'], unicode: 'σ', latex: '\\sigma', description: 'Sigma', category: 'Greek', isPopular: true },
    { trigger: ['tau'], unicode: 'τ', latex: '\\tau', description: 'Tau', category: 'Greek', isPopular: true },
    { trigger: ['upsilon'], unicode: 'υ', latex: '\\upsilon', description: 'Upsilon', category: 'Greek' },
    { trigger: ['phi'], unicode: 'φ', latex: '\\phi', description: 'Phi', category: 'Greek', isPopular: true },
    { trigger: ['chi'], unicode: 'χ', latex: '\\chi', description: 'Chi', category: 'Greek' },
    { trigger: ['psi', 'xai'], unicode: 'ψ', latex: '\\psi', description: 'Psi', category: 'Greek' },
    { trigger: ['omega'], unicode: 'ω', latex: '\\omega', description: 'Omega', category: 'Greek', isPopular: true },

    // Uppercase
    { trigger: ['Delta', 'DELTA'], unicode: 'Δ', latex: '\\Delta', description: 'Delta (uppercase)', category: 'Greek', isPopular: true },
    { trigger: ['Gamma', 'GAMMA'], unicode: 'Γ', latex: '\\Gamma', description: 'Gamma (uppercase)', category: 'Greek' },
    { trigger: ['Sigma', 'SIGMA', 'sum', 'sumof'], unicode: 'Σ', latex: '\\Sigma', description: 'Sigma (uppercase)', category: 'Greek', isPopular: true },
    { trigger: ['Omega', 'OMEGA'], unicode: 'Ω', latex: '\\Omega', description: 'Omega (uppercase)', category: 'Greek' },
    { trigger: ['Theta', 'THETA'], unicode: 'Θ', latex: '\\Theta', description: 'Theta (uppercase)', category: 'Greek' },
    { trigger: ['Lambda', 'LAMBDA'], unicode: 'Λ', latex: '\\Lambda', description: 'Lambda (uppercase)', category: 'Greek' },
    { trigger: ['Xi', 'XI'], unicode: 'Ξ', latex: '\\Xi', description: 'Xi (uppercase)', category: 'Greek' },
    { trigger: ['Pi', 'PI'], unicode: 'Π', latex: '\\Pi', description: 'Pi (uppercase)', category: 'Greek' },
    { trigger: ['Phi', 'PHI'], unicode: 'Φ', latex: '\\Phi', description: 'Phi (uppercase)', category: 'Greek' },
    { trigger: ['Psi', 'PSI'], unicode: 'Ψ', latex: '\\Psi', description: 'Psi (uppercase)', category: 'Greek' },
    { trigger: ['Upsilon', 'UPSILON'], unicode: 'Υ', latex: '\\Upsilon', description: 'Upsilon (uppercase)', category: 'Greek' },

    // Superscript modifiers
    { trigger: ['transpose', '^T', 'T_sup', 'supT'], unicode: 'ᵀ', latex: '^{\\top}', description: 'Transpose (superscript T)', category: 'Greek', isPopular: true, skipAutoConvert: true },
    { trigger: ['^1', 'sup1', 'pow1'], unicode: '¹', latex: '^{1}', description: 'Superscript 1', category: 'Greek', isPopular: true, skipAutoConvert: true },
    { trigger: ['^2', 'sup2', 'pow2', 'squared'], unicode: '²', latex: '^{2}', description: 'Superscript 2 (squared)', category: 'Greek', isPopular: true, skipAutoConvert: true },
    { trigger: ['^3', 'sup3', 'pow3', 'cubed'], unicode: '³', latex: '^{3}', description: 'Superscript 3 (cubed)', category: 'Greek', isPopular: true, skipAutoConvert: true },
    { trigger: ['^4', 'sup4', 'pow4'], unicode: '⁴', latex: '^{4}', description: 'Superscript 4', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^5', 'sup5', 'pow5'], unicode: '⁵', latex: '^{5}', description: 'Superscript 5', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^6', 'sup6', 'pow6'], unicode: '⁶', latex: '^{6}', description: 'Superscript 6', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^7', 'sup7', 'pow7'], unicode: '⁷', latex: '^{7}', description: 'Superscript 7', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^8', 'sup8', 'pow8'], unicode: '⁸', latex: '^{8}', description: 'Superscript 8', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^9', 'sup9', 'pow9'], unicode: '⁹', latex: '^{9}', description: 'Superscript 9', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^10', 'sup10', 'pow10'], unicode: '¹⁰', latex: '^{10}', description: 'Superscript 10', category: 'Greek', skipAutoConvert: true },
    { trigger: ['^-1', 'inv', 'inverse'], unicode: '⁻¹', latex: '^{-1}', description: 'Superscript -1 (inverse)', category: 'Greek', isPopular: true, skipAutoConvert: true },
    { trigger: ['^n', 'supn', 'pown'], unicode: 'ⁿ', latex: '^{n}', description: 'Superscript n', category: 'Greek', skipAutoConvert: true },

    // ==================================================================================
    // 2. OPERATORS
    // ==================================================================================
    // Basic Triggers
    { trigger: ['times', 'cross'], unicode: '×', latex: '\\times', description: 'Multiplication', category: 'Operators', isPopular: true },
    { trigger: ['div', 'divide'], unicode: '÷', latex: '\\div', description: 'Division', category: 'Operators', isPopular: true },
    { trigger: ['pm', 'plusminus'], unicode: '±', latex: '\\pm', description: 'Plus-Minus', category: 'Operators', isPopular: true },
    { trigger: ['mp'], unicode: '∓', latex: '\\mp', description: 'Minus-Plus', category: 'Operators' },
    { trigger: ['cdot', 'dot'], unicode: '⋅', latex: '\\cdot', description: 'Dot product', category: 'Operators', isPopular: true },
    { trigger: ['ast', 'star_op'], unicode: '∗', latex: '\\ast', description: 'Asterisk operator', category: 'Operators' },
    { trigger: ['star'], unicode: '⋆', latex: '\\star', description: 'Star operator', category: 'Operators' },

    // Advanced
    { trigger: ['compo', 'circ', 'circle'], unicode: '∘', latex: '\\circ', description: 'Composition / Circle', category: 'Operators', isPopular: true },
    { trigger: ['bullet'], unicode: '∙', latex: '\\bullet', description: 'Bullet', category: 'Operators' },
    { trigger: ['oplus'], unicode: '⊕', latex: '\\oplus', description: 'Direct sum', category: 'Operators' },
    { trigger: ['ominus'], unicode: '⊖', latex: '\\ominus', description: 'Direct difference', category: 'Operators' },
    { trigger: ['hadamard', 'odot'], unicode: '⊙', latex: '\\odot', description: 'Hadamard product', category: 'Operators', isPopular: true },
    { trigger: ['kroc', 'otimes'], unicode: '⊗', latex: '\\otimes', description: 'Kronecker product', category: 'Operators', isPopular: true },
    { trigger: ['oslash'], unicode: '⊘', latex: '\\oslash', description: 'Circle divide', category: 'Operators' },

    // Large Operators
    { trigger: ['prod', 'product'], unicode: '∏', latex: '\\prod', description: 'Product', category: 'Operators', isPopular: true },
    { trigger: ['coprod'], unicode: '∐', latex: '\\coprod', description: 'Coproduct', category: 'Operators' },
    { trigger: ['sum_op'], unicode: '∑', latex: '\\sum', description: 'Summation', category: 'Operators' },

    // Calculus & Analysis
    { trigger: ['sqrt', 'root'], unicode: '√', latex: '√', description: 'Square Root', category: 'Operators', isPopular: true },
    { trigger: ['int', 'integral'], unicode: '∫', latex: '\\int', description: 'Integral', category: 'Operators', isPopular: true },
    { trigger: ['iint'], unicode: '∬', latex: '\\iint', description: 'Double Integral', category: 'Operators' },
    { trigger: ['iiint'], unicode: '∭', latex: '\\iiint', description: 'Triple Integral', category: 'Operators' },
    { trigger: ['oint'], unicode: '∮', latex: '\\oint', description: 'Contour Integral', category: 'Operators' },
    { trigger: ['partial', 'part'], unicode: '∂', latex: '\\partial', description: 'Partial Derivative', category: 'Operators', isPopular: true },
    { trigger: ['nabla', 'grad', 'del'], unicode: '∇', latex: '\\nabla', description: 'Nabla/Gradient', category: 'Operators', isPopular: true },
    { trigger: ['infinity', 'inf'], unicode: '∞', latex: '\\infty', description: 'Infinity', category: 'Operators', isPopular: true },
    { trigger: ['limit', 'lim'], unicode: 'lim', latex: '\\lim', description: 'Limit', category: 'Operators', isPopular: true },

    // ==================================================================================
    // 3. RELATIONS
    // ==================================================================================
    // Equality & Inequality
    { trigger: ['gt', 'greaterthan', 'greater'], unicode: '>', latex: '>', description: 'Greater than', category: 'Relations', isPopular: true },
    { trigger: ['lt', 'lessthan', 'less'], unicode: '<', latex: '<', description: 'Less than', category: 'Relations', isPopular: true },
    { trigger: ['leq', 'le', '<=', '<=*'], unicode: '≤', latex: '\\leq', description: 'Less than or equal', category: 'Relations', isPopular: true },
    { trigger: ['geq', 'ge', '>=', '>=*'], unicode: '≥', latex: '\\geq', description: 'Greater than or equal', category: 'Relations', isPopular: true },
    { trigger: ['neq', 'ne', 'notequal', '!='], unicode: '≠', latex: '\\neq', description: 'Not equal', category: 'Relations', isPopular: true },
    { trigger: ['approx', '~='], unicode: '≈', latex: '\\approx', description: 'Approximately equal', category: 'Relations', isPopular: true },
    { trigger: ['equiv'], unicode: '≡', latex: '\\equiv', description: 'Equivalent', category: 'Relations' },
    { trigger: ['sim'], unicode: '∼', latex: '\\sim', description: 'Similar', category: 'Relations' },
    { trigger: ['simeq'], unicode: '≃', latex: '\\simeq', description: 'Similar or equal', category: 'Relations' },
    { trigger: ['cong'], unicode: '≅', latex: '\\cong', description: 'Congruent', category: 'Relations' },
    { trigger: ['proportional', 'prop'], unicode: '∝', latex: '\\propto', description: 'Proportional to', category: 'Relations', isPopular: true },
    { trigger: ['ll'], unicode: '≪', latex: '\\ll', description: 'Much less than', category: 'Relations' },
    { trigger: ['gg'], unicode: '≫', latex: '\\gg', description: 'Much greater than', category: 'Relations' },

    // Set Relations
    { trigger: ['in', 'element', 'belong'], unicode: '∈', latex: '\\in', description: 'Element of', category: 'Relations', isPopular: true },
    { trigger: ['notin'], unicode: '∉', latex: '\\notin', description: 'Not element of', category: 'Relations', isPopular: true },
    { trigger: ['subset', 'subsetof'], unicode: '⊂', latex: '\\subset', description: 'Subset', category: 'Relations' },
    { trigger: ['subseteq', 'subequa'], unicode: '⊆', latex: '\\subseteq', description: 'Subset or equal', category: 'Relations', isPopular: true },
    { trigger: ['nsubseteq'], unicode: '⊈', latex: '\\nsubseteq', description: 'Not subset or equal', category: 'Relations' },
    { trigger: ['supset'], unicode: '⊃', latex: '\\supset', description: 'Superset', category: 'Relations' },
    { trigger: ['supseteq'], unicode: '⊇', latex: '\\supseteq', description: 'Superset or equal', category: 'Relations' },

    // Generalized Inequalities (Convex Optimization)
    { trigger: ['preceq', 'curlyeq', 'gleq'], unicode: '⪯', latex: '\\preceq', description: 'Precedes or equal (generalized ≤)', category: 'Relations', isPopular: true },
    { trigger: ['succeq', 'curlygeq', 'ggeq'], unicode: '⪰', latex: '\\succeq', description: 'Succeeds or equal (generalized ≥)', category: 'Relations', isPopular: true },
    { trigger: ['prec', 'curlylt'], unicode: '≺', latex: '\\prec', description: 'Precedes (generalized <)', category: 'Relations' },
    { trigger: ['succ', 'curlygt'], unicode: '≻', latex: '\\succ', description: 'Succeeds (generalized >)', category: 'Relations' },
    { trigger: ['npreceq'], unicode: '⋠', latex: '\\npreceq', description: 'Not precedes or equal', category: 'Relations' },
    { trigger: ['nsucceq'], unicode: '⋡', latex: '\\nsucceq', description: 'Not succeeds or equal', category: 'Relations' },

    // ==================================================================================
    // 4. LOGIC
    // ==================================================================================
    { trigger: ['forall', 'all'], unicode: '∀', latex: '\\forall', description: 'For all', category: 'Logic', isPopular: true },
    { trigger: ['exists'], unicode: '∃', latex: '\\exists', description: 'There exists', category: 'Logic', isPopular: true },
    { trigger: ['nexists'], unicode: '∄', latex: '\\nexists', description: 'Does not exist', category: 'Logic' },
    { trigger: ['neg', 'not'], unicode: '¬', latex: '\\neg', description: 'Negation', category: 'Logic' },
    { trigger: ['land', 'and'], unicode: '∧', latex: '\\land', description: 'Logical AND', category: 'Logic' },
    { trigger: ['lor', 'or'], unicode: '∨', latex: '\\lor', description: 'Logical OR', category: 'Logic' },
    { trigger: ['implies'], unicode: '⟹', latex: '\\implies', description: 'Implies (Long)', category: 'Logic' },
    { trigger: ['iff'], unicode: '⟺', latex: '\\iff', description: 'If and only if (Long)', category: 'Logic' },
    { trigger: ['therefore'], unicode: '∴', latex: '\\therefore', description: 'Therefore', category: 'Logic' },
    { trigger: ['because', 'bcs'], unicode: '∵', latex: '\\because', description: 'Because', category: 'Logic' },

    // ==================================================================================
    // 5. SETS (Blackboard Bold, etc.)
    // ==================================================================================
    { trigger: ['emptyset', 'empty'], unicode: '∅', latex: '\\emptyset', description: 'Empty set', category: 'Sets', isPopular: true },
    { trigger: ['inter', 'cap'], unicode: '∩', latex: '\\cap', description: 'Intersection', category: 'Sets', isPopular: true },
    { trigger: ['union', 'cup'], unicode: '∪', latex: '\\cup', description: 'Union', category: 'Sets', isPopular: true },
    { trigger: ['setminus'], unicode: '∖', latex: '\\setminus', description: 'Set minus', category: 'Sets' },

    // Number Sets
    { trigger: ['RR', 'reals'], unicode: 'ℝ', latex: '\\mathbb{R}', description: 'Real Numbers', category: 'Sets', isPopular: true },
    { trigger: ['NN', 'naturals'], unicode: 'ℕ', latex: '\\mathbb{N}', description: 'Natural Numbers', category: 'Sets', isPopular: true },
    { trigger: ['ZZ', 'integers'], unicode: 'ℤ', latex: '\\mathbb{Z}', description: 'Integers', category: 'Sets', isPopular: true },
    { trigger: ['QQ', 'rationals'], unicode: 'ℚ', latex: '\\mathbb{Q}', description: 'Rational Numbers', category: 'Sets' },
    { trigger: ['CC', 'complex'], unicode: 'ℂ', latex: '\\mathbb{C}', description: 'Complex Numbers', category: 'Sets' },

    // ==================================================================================
    // 6. ARROWS
    // ==================================================================================
    { trigger: ['rightarrow', 'to', '->'], unicode: '→', latex: '\\rightarrow', description: 'Right arrow', category: 'Arrows', isPopular: true },
    { trigger: ['leftarrow', '<-', '<*'], unicode: '←', latex: '\\leftarrow', description: 'Left arrow', category: 'Arrows', isPopular: true },
    { trigger: ['uparrow'], unicode: '↑', latex: '\\uparrow', description: 'Up arrow', category: 'Arrows' },
    { trigger: ['downarrow'], unicode: '↓', latex: '\\downarrow', description: 'Down arrow', category: 'Arrows' },
    { trigger: ['leftrightarrow', '<->'], unicode: '↔', latex: '\\leftrightarrow', description: 'Left-Right arrow', category: 'Arrows' },

    // Double Arrows
    { trigger: ['Rightarrow', '=>'], unicode: '⇒', latex: '\\Rightarrow', description: 'Implies', category: 'Arrows', isPopular: true },
    { trigger: ['Leftarrow', '<='], unicode: '⇐', latex: '\\Leftarrow', description: 'Implied by', category: 'Arrows' },
    { trigger: ['Leftrightarrow', '<=>'], unicode: '⇔', latex: '\\Leftrightarrow', description: 'If and only if', category: 'Arrows', isPopular: true },

    // Long Arrows
    { trigger: ['longto', 'longrightarrow'], unicode: '⟶', latex: '\\longrightarrow', description: 'Long Right arrow', category: 'Arrows' },
    { trigger: ['longfrom', 'longleftarrow'], unicode: '⟵', latex: '\\longleftarrow', description: 'Long Left arrow', category: 'Arrows' },
    { trigger: ['mapsto'], unicode: '↦', latex: '\\mapsto', description: 'Maps to', category: 'Arrows' },

    // ==================================================================================
    // 7. GEOMETRY
    // ==================================================================================
    { trigger: ['angle'], unicode: '∠', latex: '\\angle', description: 'Angle', category: 'Geometry' },
    { trigger: ['triangle'], unicode: '△', latex: '\\triangle', description: 'Triangle', category: 'Geometry' },
    { trigger: ['perp'], unicode: '⊥', latex: '\\perp', description: 'Perpendicular', category: 'Geometry' },
    { trigger: ['parallel', 'para'], unicode: '∥', latex: '\\parallel', description: 'Parallel', category: 'Geometry' },
    { trigger: ['degree', 'deg'], unicode: '°', latex: '^\\circ', description: 'Degree', category: 'Geometry', isPopular: true, skipAutoConvert: true },

    // ==================================================================================
    // 8. MISC & SPECIAL
    // ==================================================================================
    // Dots
    { trigger: ['dots', 'ldots'], unicode: '…', latex: '\\dots', description: 'Ellipsis (lower)', category: 'Misc' },
    { trigger: ['cdots'], unicode: '⋯', latex: '\\cdots', description: 'Ellipsis (centered)', category: 'Misc' },
    { trigger: ['vdots'], unicode: '⋮', latex: '\\vdots', description: 'Vertical dots', category: 'Misc' },
    { trigger: ['ddots'], unicode: '⋱', latex: '\\ddots', description: 'Diagonal dots', category: 'Misc' },

    // Accents & Decorations
    { trigger: ['hat'], unicode: '̂', latex: '\\hat', description: 'Hat accent', category: 'Misc' },
    { trigger: ['bar'], unicode: '¯', latex: '¯', description: 'Bar accent', category: 'Misc' },
    { trigger: ['tilde'], unicode: '∼', latex: '\\sim', description: 'Tilde accent', category: 'Misc' },
    { trigger: ['vec'], unicode: '→', latex: '→', description: 'Vector accent', category: 'Misc' },

    // Other
    { trigger: ['prime'], unicode: '′', latex: '\\prime', description: 'Prime', category: 'Misc' },
    { trigger: ['ell'], unicode: 'ℓ', latex: '\\ell', description: 'Script l', category: 'Misc' },
    { trigger: ['hbar'], unicode: 'ℏ', latex: '\\hbar', description: 'H-bar (Planck)', category: 'Misc' },
    { trigger: ['dagger'], unicode: '†', latex: '\\dagger', description: 'Dagger', category: 'Misc' },

    // Special User Requests
    { trigger: ['G_cur'], unicode: '𝒢', latex: '\\mathcal{G}', description: 'Calligraphic G', category: 'Special', isPopular: true },
    { trigger: ['x_cap'], unicode: '𝐱', latex: '\\mathbf{x}', description: 'Bold x', category: 'Special', isPopular: true },
    { trigger: ['X_cap'], unicode: '𝐗', latex: '\\mathbf{X}', description: 'Bold X', category: 'Special', isPopular: true },

    // ==================================================================================
    // CALLIGRAPHIC — \mathcal{A-Z} — đầy đủ 26 chữ cái
    // ==================================================================================
    { trigger: ['calA', 'A_cal'],           unicode: '𝒜', latex: '\\mathcal{A}', description: 'Calligraphic A', category: 'Calligraphic' },
    { trigger: ['calB', 'B_cal'],           unicode: 'ℬ', latex: '\\mathcal{B}', description: 'Calligraphic B', category: 'Calligraphic' },
    { trigger: ['calC', 'C_cal'],           unicode: '𝒞', latex: '\\mathcal{C}', description: 'Calligraphic C', category: 'Calligraphic' },
    { trigger: ['calD', 'D_cal', 'calData'], unicode: '𝒟', latex: '\\mathcal{D}', description: 'Calligraphic D — Dataset', category: 'Calligraphic', isPopular: true },
    { trigger: ['calE', 'E_cal', 'calExp'],  unicode: 'ℰ', latex: '\\mathcal{E}', description: 'Calligraphic E — Expectation / Edge set', category: 'Calligraphic', isPopular: true },
    { trigger: ['calF', 'F_cal'],           unicode: 'ℱ', latex: '\\mathcal{F}', description: 'Calligraphic F — Feature space / Filtration', category: 'Calligraphic' },
    { trigger: ['calG', 'G_cal'],           unicode: '𝒢', latex: '\\mathcal{G}', description: 'Calligraphic G', category: 'Calligraphic' },
    { trigger: ['calH', 'H_cal', 'calHyp'], unicode: 'ℋ', latex: '\\mathcal{H}', description: 'Calligraphic H — Hypothesis space / Hessian', category: 'Calligraphic', isPopular: true },
    { trigger: ['calI', 'I_cal'],           unicode: 'ℐ', latex: '\\mathcal{I}', description: 'Calligraphic I', category: 'Calligraphic' },
    { trigger: ['calJ', 'J_cal'],           unicode: '𝒥', latex: '\\mathcal{J}', description: 'Calligraphic J', category: 'Calligraphic' },
    { trigger: ['calK', 'K_cal'],           unicode: '𝒦', latex: '\\mathcal{K}', description: 'Calligraphic K', category: 'Calligraphic' },
    { trigger: ['calL', 'L_cal', 'calLoss'], unicode: 'ℒ', latex: '\\mathcal{L}', description: 'Calligraphic L — Loss function', category: 'Calligraphic', isPopular: true },
    { trigger: ['calM', 'M_cal'],           unicode: 'ℳ', latex: '\\mathcal{M}', description: 'Calligraphic M — Manifold / Model', category: 'Calligraphic' },
    { trigger: ['calN', 'N_cal', 'calNorm'], unicode: '𝒩', latex: '\\mathcal{N}', description: 'Calligraphic N — Normal distribution', category: 'Calligraphic', isPopular: true },
    { trigger: ['calO', 'O_cal', 'bigO'],   unicode: '𝒪', latex: '\\mathcal{O}', description: 'Calligraphic O — Big-O complexity', category: 'Calligraphic', isPopular: true },
    { trigger: ['calP', 'P_cal'],           unicode: '𝒫', latex: '\\mathcal{P}', description: 'Calligraphic P — Probability measure', category: 'Calligraphic' },
    { trigger: ['calQ', 'Q_cal'],           unicode: '𝒬', latex: '\\mathcal{Q}', description: 'Calligraphic Q', category: 'Calligraphic' },
    { trigger: ['calR', 'R_cal'],           unicode: 'ℛ', latex: '\\mathcal{R}', description: 'Calligraphic R — Risk / Reward', category: 'Calligraphic' },
    { trigger: ['calS', 'S_cal'],           unicode: '𝒮', latex: '\\mathcal{S}', description: 'Calligraphic S — State space', category: 'Calligraphic' },
    { trigger: ['calT', 'T_cal'],           unicode: '𝒯', latex: '\\mathcal{T}', description: 'Calligraphic T — Transformation', category: 'Calligraphic' },
    { trigger: ['calU', 'U_cal'],           unicode: '𝒰', latex: '\\mathcal{U}', description: 'Calligraphic U', category: 'Calligraphic' },
    { trigger: ['calV', 'V_cal'],           unicode: '𝒱', latex: '\\mathcal{V}', description: 'Calligraphic V — Vocabulary / Value', category: 'Calligraphic' },
    { trigger: ['calW', 'W_cal'],           unicode: '𝒲', latex: '\\mathcal{W}', description: 'Calligraphic W — Weight space', category: 'Calligraphic' },
    { trigger: ['calX', 'X_cal'],           unicode: '𝒳', latex: '\\mathcal{X}', description: 'Calligraphic X — Input space', category: 'Calligraphic' },
    { trigger: ['calY', 'Y_cal'],           unicode: '𝒴', latex: '\\mathcal{Y}', description: 'Calligraphic Y — Output space', category: 'Calligraphic' },
    { trigger: ['calZ', 'Z_cal'],           unicode: '𝒵', latex: '\\mathcal{Z}', description: 'Calligraphic Z — Latent space', category: 'Calligraphic' },

    // ==================================================================================
    // BOLD LATIN — \mathbf{letter} — vectors (lowercase) and matrices (uppercase)
    // ==================================================================================
    { trigger: ['abold', 'a_bold'], unicode: '𝐚', latex: '\\mathbf{a}', description: 'Bold a (vector)', category: 'Bold' },
    { trigger: ['bbold', 'b_bold'], unicode: '𝐛', latex: '\\mathbf{b}', description: 'Bold b (vector)', category: 'Bold' },
    { trigger: ['cbold', 'c_bold'], unicode: '𝐜', latex: '\\mathbf{c}', description: 'Bold c (vector)', category: 'Bold' },
    { trigger: ['dbold', 'd_bold'], unicode: '𝐝', latex: '\\mathbf{d}', description: 'Bold d (vector)', category: 'Bold' },
    { trigger: ['ebold', 'e_bold'], unicode: '𝐞', latex: '\\mathbf{e}', description: 'Bold e (vector)', category: 'Bold' },
    { trigger: ['fbold', 'f_bold'], unicode: '𝐟', latex: '\\mathbf{f}', description: 'Bold f (vector)', category: 'Bold' },
    { trigger: ['gbold', 'g_bold'], unicode: '𝐠', latex: '\\mathbf{g}', description: 'Bold g (vector)', category: 'Bold' },
    { trigger: ['hbold', 'h_bold'], unicode: '𝐡', latex: '\\mathbf{h}', description: 'Bold h (vector)', category: 'Bold' },
    { trigger: ['ibold', 'i_bold'], unicode: '𝐢', latex: '\\mathbf{i}', description: 'Bold i (vector)', category: 'Bold' },
    { trigger: ['jbold', 'j_bold'], unicode: '𝐣', latex: '\\mathbf{j}', description: 'Bold j (vector)', category: 'Bold' },
    { trigger: ['kbold', 'k_bold'], unicode: '𝐤', latex: '\\mathbf{k}', description: 'Bold k (vector)', category: 'Bold' },
    { trigger: ['lbold', 'l_bold'], unicode: '𝐥', latex: '\\mathbf{l}', description: 'Bold l (vector)', category: 'Bold' },
    { trigger: ['mbold', 'm_bold'], unicode: '𝐦', latex: '\\mathbf{m}', description: 'Bold m (vector)', category: 'Bold' },
    { trigger: ['nbold', 'n_bold'], unicode: '𝐧', latex: '\\mathbf{n}', description: 'Bold n (vector)', category: 'Bold' },
    { trigger: ['obold', 'o_bold'], unicode: '𝐨', latex: '\\mathbf{o}', description: 'Bold o (vector)', category: 'Bold' },
    { trigger: ['pbold', 'p_bold'], unicode: '𝐩', latex: '\\mathbf{p}', description: 'Bold p (vector)', category: 'Bold' },
    { trigger: ['qbold', 'q_bold'], unicode: '𝐪', latex: '\\mathbf{q}', description: 'Bold q (vector)', category: 'Bold' },
    { trigger: ['rbold', 'r_bold'], unicode: '𝐫', latex: '\\mathbf{r}', description: 'Bold r (vector)', category: 'Bold' },
    { trigger: ['sbold', 's_bold'], unicode: '𝐬', latex: '\\mathbf{s}', description: 'Bold s (vector)', category: 'Bold' },
    { trigger: ['tbold', 't_bold'], unicode: '𝐭', latex: '\\mathbf{t}', description: 'Bold t (vector)', category: 'Bold' },
    { trigger: ['ubold', 'u_bold'], unicode: '𝐮', latex: '\\mathbf{u}', description: 'Bold u (vector)', category: 'Bold' },
    { trigger: ['vbold', 'v_bold'], unicode: '𝐯', latex: '\\mathbf{v}', description: 'Bold v (vector)', category: 'Bold' },
    { trigger: ['wbold', 'w_bold'], unicode: '𝐰', latex: '\\mathbf{w}', description: 'Bold w (vector)', category: 'Bold' },
    { trigger: ['xbold', 'x_bold'], unicode: '𝐱', latex: '\\mathbf{x}', description: 'Bold x (vector)', category: 'Bold', isPopular: true },
    { trigger: ['ybold', 'y_bold'], unicode: '𝐲', latex: '\\mathbf{y}', description: 'Bold y (vector)', category: 'Bold', isPopular: true },
    { trigger: ['zbold', 'z_bold'], unicode: '𝐳', latex: '\\mathbf{z}', description: 'Bold z (vector)', category: 'Bold' },
    { trigger: ['Abold', 'A_bold'], unicode: '𝐀', latex: '\\mathbf{A}', description: 'Bold A (matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Bbold', 'B_bold'], unicode: '𝐁', latex: '\\mathbf{B}', description: 'Bold B (matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Cbold', 'C_bold'], unicode: '𝐂', latex: '\\mathbf{C}', description: 'Bold C (matrix)', category: 'Bold' },
    { trigger: ['Dbold', 'D_bold'], unicode: '𝐃', latex: '\\mathbf{D}', description: 'Bold D (matrix)', category: 'Bold' },
    { trigger: ['Ebold', 'E_bold'], unicode: '𝐄', latex: '\\mathbf{E}', description: 'Bold E (matrix)', category: 'Bold' },
    { trigger: ['Fbold', 'F_bold'], unicode: '𝐅', latex: '\\mathbf{F}', description: 'Bold F (matrix)', category: 'Bold' },
    { trigger: ['Gbold', 'G_bold'], unicode: '𝐆', latex: '\\mathbf{G}', description: 'Bold G (matrix)', category: 'Bold' },
    { trigger: ['Hbold', 'H_bold'], unicode: '𝐇', latex: '\\mathbf{H}', description: 'Bold H (matrix)', category: 'Bold' },
    { trigger: ['Ibold', 'I_bold'], unicode: '𝐈', latex: '\\mathbf{I}', description: 'Bold I (identity matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Jbold', 'J_bold'], unicode: '𝐉', latex: '\\mathbf{J}', description: 'Bold J (matrix)', category: 'Bold' },
    { trigger: ['Kbold', 'K_bold'], unicode: '𝐊', latex: '\\mathbf{K}', description: 'Bold K (kernel matrix)', category: 'Bold' },
    { trigger: ['Lblod', 'L_bold'], unicode: '𝐋', latex: '\\mathbf{L}', description: 'Bold L (matrix)', category: 'Bold' },
    { trigger: ['Mbold', 'M_bold'], unicode: '𝐌', latex: '\\mathbf{M}', description: 'Bold M (matrix)', category: 'Bold' },
    { trigger: ['Nbold', 'N_bold'], unicode: '𝐍', latex: '\\mathbf{N}', description: 'Bold N (matrix)', category: 'Bold' },
    { trigger: ['Obold', 'O_bold'], unicode: '𝐎', latex: '\\mathbf{O}', description: 'Bold O (matrix)', category: 'Bold' },
    { trigger: ['Pbold', 'P_bold'], unicode: '𝐏', latex: '\\mathbf{P}', description: 'Bold P (projection matrix)', category: 'Bold' },
    { trigger: ['Qbold', 'Q_bold'], unicode: '𝐐', latex: '\\mathbf{Q}', description: 'Bold Q (matrix)', category: 'Bold' },
    { trigger: ['Rbold', 'R_bold'], unicode: '𝐑', latex: '\\mathbf{R}', description: 'Bold R (matrix)', category: 'Bold' },
    { trigger: ['Sbold', 'S_bold'], unicode: '𝐒', latex: '\\mathbf{S}', description: 'Bold S (matrix)', category: 'Bold' },
    { trigger: ['Tbold', 'T_bold'], unicode: '𝐓', latex: '\\mathbf{T}', description: 'Bold T (matrix)', category: 'Bold' },
    { trigger: ['Ubold', 'U_bold'], unicode: '𝐔', latex: '\\mathbf{U}', description: 'Bold U (matrix)', category: 'Bold' },
    { trigger: ['Vbold', 'V_bold'], unicode: '𝐕', latex: '\\mathbf{V}', description: 'Bold V (matrix)', category: 'Bold' },
    { trigger: ['Wbold', 'W_bold'], unicode: '𝐖', latex: '\\mathbf{W}', description: 'Bold W (weight matrix)', category: 'Bold' },
    { trigger: ['Xbold', 'X_bold'], unicode: '𝐗', latex: '\\mathbf{X}', description: 'Bold X (data matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Ybold', 'Y_bold'], unicode: '𝐘', latex: '\\mathbf{Y}', description: 'Bold Y (matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Zbold', 'Z_bold'], unicode: '𝐙', latex: '\\mathbf{Z}', description: 'Bold Z (matrix)', category: 'Bold' },

    // Greek bold uppercase (matrices)
    { trigger: ['Sigmabold', 'Sigma_bold', 'Sbold_greek'], unicode: '𝚺', latex: '\\boldsymbol{\\Sigma}', description: 'Bold Σ (covariance matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Lambdabold', 'Lambda_bold', 'Lbold_greek'], unicode: '𝚲', latex: '\\boldsymbol{\\Lambda}', description: 'Bold Λ (diagonal matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Gammabold', 'Gamma_bold'], unicode: '𝚪', latex: '\\boldsymbol{\\Gamma}', description: 'Bold Γ (matrix)', category: 'Bold' },
    { trigger: ['Deltabold', 'Delta_bold'], unicode: '𝚫', latex: '\\boldsymbol{\\Delta}', description: 'Bold Δ (matrix)', category: 'Bold' },
    { trigger: ['Thetabold', 'Theta_bold'], unicode: '𝚯', latex: '\\boldsymbol{\\Theta}', description: 'Bold Θ (parameter matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Omegabold', 'Omega_bold'], unicode: '𝛀', latex: '\\boldsymbol{\\Omega}', description: 'Bold Ω (matrix)', category: 'Bold' },
    { trigger: ['Phibold', 'Phi_bold'], unicode: '𝚽', latex: '\\boldsymbol{\\Phi}', description: 'Bold Φ (feature matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Psibold', 'Psi_bold'], unicode: '𝚿', latex: '\\boldsymbol{\\Psi}', description: 'Bold Ψ (matrix)', category: 'Bold' },
    { trigger: ['Pibold', 'Pi_bold'], unicode: '𝚷', latex: '\\boldsymbol{\\Pi}', description: 'Bold Π (transition/projection matrix)', category: 'Bold', isPopular: true },
    { trigger: ['Alphabold', 'Alpha_bold'], unicode: '𝚨', latex: '\\boldsymbol{\\Alpha}', description: 'Bold Α (matrix)', category: 'Bold' },
    { trigger: ['Betabold', 'Beta_bold'], unicode: '𝚩', latex: '\\boldsymbol{\\Beta}', description: 'Bold Β (matrix)', category: 'Bold' },
    { trigger: ['Etabold', 'Eta_bold'], unicode: '𝚮', latex: '\\boldsymbol{\\Eta}', description: 'Bold Η (matrix)', category: 'Bold' },
    { trigger: ['Kappabold', 'Kappa_bold'], unicode: '𝚱', latex: '\\boldsymbol{\\Kappa}', description: 'Bold Κ (matrix)', category: 'Bold' },
    { trigger: ['Mubold', 'Mu_bold'], unicode: '𝚳', latex: '\\boldsymbol{\\Mu}', description: 'Bold Μ (matrix)', category: 'Bold' },
    { trigger: ['Nubold', 'Nu_bold'], unicode: '𝚴', latex: '\\boldsymbol{\\Nu}', description: 'Bold Ν (matrix)', category: 'Bold' },
    { trigger: ['Xibold', 'Xi_bold'], unicode: '𝚵', latex: '\\boldsymbol{\\Xi}', description: 'Bold Ξ (matrix)', category: 'Bold' },
    { trigger: ['Rhobbold', 'Rho_bold'], unicode: '𝚸', latex: '\\boldsymbol{\\Rho}', description: 'Bold Ρ (matrix)', category: 'Bold' },
    { trigger: ['Taubold', 'Tau_bold'], unicode: '𝚻', latex: '\\boldsymbol{\\Tau}', description: 'Bold Τ (matrix)', category: 'Bold' },
    { trigger: ['Upsilonbold', 'Upsilon_bold'], unicode: '𝚼', latex: '\\boldsymbol{\\Upsilon}', description: 'Bold Υ (matrix)', category: 'Bold' },
    { trigger: ['Chibold', 'Chi_bold'], unicode: '𝚾', latex: '\\boldsymbol{\\Chi}', description: 'Bold Χ (matrix)', category: 'Bold' },

    // Greek bold lowercase (vectors)
    { trigger: ['mubold', 'mu_bold'], unicode: '𝛍', latex: '\\boldsymbol{\\mu}', description: 'Bold μ (mean vector)', category: 'Bold', isPopular: true },
    { trigger: ['epsbold', 'epsilon_bold', 'eps_bold'], unicode: '𝛜', latex: '\\boldsymbol{\\varepsilon}', description: 'Bold ε (error vector)', category: 'Bold', isPopular: true },
    { trigger: ['lambdabold', 'lambda_bold'], unicode: '𝛌', latex: '\\boldsymbol{\\lambda}', description: 'Bold λ (eigenvalue vector)', category: 'Bold', isPopular: true },
    { trigger: ['alphabold', 'alpha_bold'], unicode: '𝛂', latex: '\\boldsymbol{\\alpha}', description: 'Bold α (vector)', category: 'Bold' },
    { trigger: ['betabold', 'beta_bold'], unicode: '𝛃', latex: '\\boldsymbol{\\beta}', description: 'Bold β (coefficient vector)', category: 'Bold', isPopular: true },
    { trigger: ['thetabold', 'theta_bold'], unicode: '𝛉', latex: '\\boldsymbol{\\theta}', description: 'Bold θ (parameter vector)', category: 'Bold', isPopular: true },
    { trigger: ['phibold', 'phi_bold'], unicode: '𝛗', latex: '\\boldsymbol{\\phi}', description: 'Bold φ (feature vector)', category: 'Bold', isPopular: true },
    { trigger: ['psibold', 'psi_bold'], unicode: '𝛙', latex: '\\boldsymbol{\\psi}', description: 'Bold ψ (vector)', category: 'Bold' },
    { trigger: ['omegabold', 'omega_bold'], unicode: '𝛚', latex: '\\boldsymbol{\\omega}', description: 'Bold ω (vector)', category: 'Bold' },
    { trigger: ['nubold', 'nu_bold'], unicode: '𝛎', latex: '\\boldsymbol{\\nu}', description: 'Bold ν (vector)', category: 'Bold' },
    { trigger: ['xibold', 'xi_bold'], unicode: '𝛏', latex: '\\boldsymbol{\\xi}', description: 'Bold ξ (vector)', category: 'Bold' },
    { trigger: ['sigmabold', 'sigma_bold'], unicode: '𝛔', latex: '\\boldsymbol{\\sigma}', description: 'Bold σ (vector)', category: 'Bold' },
    { trigger: ['taubold', 'tau_bold'], unicode: '𝛕', latex: '\\boldsymbol{\\tau}', description: 'Bold τ (vector)', category: 'Bold' },
    { trigger: ['gammabold', 'gamma_bold'], unicode: '𝛄', latex: '\\boldsymbol{\\gamma}', description: 'Bold γ (discount/gradient vector)', category: 'Bold', isPopular: true },
    { trigger: ['deltabold', 'delta_bold'], unicode: '𝛅', latex: '\\boldsymbol{\\delta}', description: 'Bold δ (perturbation/TD error vector)', category: 'Bold', isPopular: true },
    { trigger: ['etabold', 'eta_bold'], unicode: '𝛈', latex: '\\boldsymbol{\\eta}', description: 'Bold η (learning rate vector)', category: 'Bold', isPopular: true },
    { trigger: ['kappabold', 'kappa_bold'], unicode: '𝛋', latex: '\\boldsymbol{\\kappa}', description: 'Bold κ (vector)', category: 'Bold' },
    { trigger: ['rhobold', 'rho_bold'], unicode: '𝛒', latex: '\\boldsymbol{\\rho}', description: 'Bold ρ (correlation vector)', category: 'Bold' },
    { trigger: ['chibold', 'chi_bold'], unicode: '𝛘', latex: '\\boldsymbol{\\chi}', description: 'Bold χ (vector)', category: 'Bold' },
    { trigger: ['zetabold', 'zeta_bold'], unicode: '𝛇', latex: '\\boldsymbol{\\zeta}', description: 'Bold ζ (vector)', category: 'Bold' },
    { trigger: ['iotabold', 'iota_bold'], unicode: '𝛊', latex: '\\boldsymbol{\\iota}', description: 'Bold ι (vector)', category: 'Bold' },
    { trigger: ['upsilonbold', 'upsilon_bold'], unicode: '𝛖', latex: '\\boldsymbol{\\upsilon}', description: 'Bold υ (vector)', category: 'Bold' },
    { trigger: ['pibold', 'pi_bold'], unicode: '𝛑', latex: '\\boldsymbol{\\pi}', description: 'Bold π (policy vector in RL)', category: 'Bold', isPopular: true },

    // Bold digits (zero/one vectors)
    { trigger: ['0bold', 'zero_bold', 'zerovec'], unicode: '𝟎', latex: '\\mathbf{0}', description: 'Bold 0 (zero vector/matrix)', category: 'Bold', isPopular: true },
    { trigger: ['1bold', 'one_bold', 'onevec'], unicode: '𝟏', latex: '\\mathbf{1}', description: 'Bold 1 (ones vector)', category: 'Bold', isPopular: true },

    // Matrix operators
    { trigger: ['diag'], unicode: 'diag', latex: '\\text{diag}', description: 'diag (diagonal operator)', category: 'Bold', isPopular: true },
    { trigger: ['tr', 'trace'], unicode: 'tr', latex: '\\text{tr}', description: 'tr (matrix trace)', category: 'Bold', isPopular: true },
    { trigger: ['rank'], unicode: 'rank', latex: '\\text{rank}', description: 'rank (matrix rank)', category: 'Bold' },
    { trigger: ['span'], unicode: 'span', latex: '\\text{span}', description: 'span (linear span)', category: 'Bold' },
    { trigger: ['null', 'ker'], unicode: 'null', latex: '\\text{null}', description: 'null (null space)', category: 'Bold' },
    { trigger: ['col'], unicode: 'col', latex: '\\text{col}', description: 'col (column space)', category: 'Bold' },
    { trigger: ['row'], unicode: 'row', latex: '\\text{row}', description: 'row (row space)', category: 'Bold' },

    // ==================================================================================
    // HAT (circumflex accent) — estimators and unit vectors
    // ==================================================================================
    { trigger: ['ahat', 'a_hat'], unicode: 'â', latex: '\\hat{a}', description: 'a-hat', category: 'Hat' },
    { trigger: ['bhat', 'b_hat'], unicode: 'b̂', latex: '\\hat{b}', description: 'b-hat', category: 'Hat' },
    { trigger: ['chat', 'c_hat'], unicode: 'ĉ', latex: '\\hat{c}', description: 'c-hat', category: 'Hat' },
    { trigger: ['ehat', 'e_hat'], unicode: 'ê', latex: '\\hat{e}', description: 'e-hat', category: 'Hat' },
    { trigger: ['fhat', 'f_hat'], unicode: 'f̂', latex: '\\hat{f}', description: 'f-hat', category: 'Hat' },
    { trigger: ['ghat', 'g_hat'], unicode: 'ĝ', latex: '\\hat{g}', description: 'g-hat', category: 'Hat' },
    { trigger: ['hhat', 'h_hat'], unicode: 'ĥ', latex: '\\hat{h}', description: 'h-hat', category: 'Hat' },
    { trigger: ['ihat', 'i_hat'], unicode: 'î', latex: '\\hat{\\imath}', description: 'i-hat (unit vector)', category: 'Hat', isPopular: true },
    { trigger: ['jhat', 'j_hat'], unicode: 'ĵ', latex: '\\hat{\\jmath}', description: 'j-hat (unit vector)', category: 'Hat', isPopular: true },
    { trigger: ['khat', 'k_hat'], unicode: 'k̂', latex: '\\hat{k}', description: 'k-hat (unit vector)', category: 'Hat', isPopular: true },
    { trigger: ['lhat', 'l_hat'], unicode: 'l̂', latex: '\\hat{l}', description: 'l-hat', category: 'Hat' },
    { trigger: ['mhat', 'm_hat'], unicode: 'm̂', latex: '\\hat{m}', description: 'm-hat', category: 'Hat' },
    { trigger: ['nhat', 'n_hat'], unicode: 'n̂', latex: '\\hat{n}', description: 'n-hat (unit normal)', category: 'Hat', isPopular: true },
    { trigger: ['phat', 'p_hat'], unicode: 'p̂', latex: '\\hat{p}', description: 'p-hat (proportion estimate)', category: 'Hat', isPopular: true },
    { trigger: ['qhat', 'q_hat'], unicode: 'q̂', latex: '\\hat{q}', description: 'q-hat', category: 'Hat' },
    { trigger: ['rhat', 'r_hat'], unicode: 'r̂', latex: '\\hat{r}', description: 'r-hat', category: 'Hat' },
    { trigger: ['shat', 's_hat'], unicode: 'ŝ', latex: '\\hat{s}', description: 's-hat', category: 'Hat' },
    { trigger: ['that', 't_hat'], unicode: 't̂', latex: '\\hat{t}', description: 't-hat', category: 'Hat' },
    { trigger: ['uhat', 'u_hat'], unicode: 'û', latex: '\\hat{u}', description: 'u-hat', category: 'Hat' },
    { trigger: ['vhat', 'v_hat'], unicode: 'v̂', latex: '\\hat{v}', description: 'v-hat', category: 'Hat' },
    { trigger: ['what', 'w_hat'], unicode: 'ŵ', latex: '\\hat{w}', description: 'w-hat', category: 'Hat' },
    { trigger: ['xhat', 'x_hat'], unicode: 'x̂', latex: '\\hat{x}', description: 'x-hat', category: 'Hat', isPopular: true },
    { trigger: ['yhat', 'y_hat'], unicode: 'ŷ', latex: '\\hat{y}', description: 'y-hat (prediction)', category: 'Hat', isPopular: true },
    { trigger: ['zhat', 'z_hat'], unicode: 'ẑ', latex: '\\hat{z}', description: 'z-hat', category: 'Hat' },
    { trigger: ['Ahat', 'A_hat'], unicode: 'Â', latex: '\\hat{A}', description: 'A-hat', category: 'Hat' },
    { trigger: ['Bhat', 'B_hat'], unicode: 'B̂', latex: '\\hat{B}', description: 'B-hat', category: 'Hat' },
    { trigger: ['Chat', 'C_hat'], unicode: 'Ĉ', latex: '\\hat{C}', description: 'C-hat', category: 'Hat' },
    { trigger: ['Ihat', 'I_hat'], unicode: 'Î', latex: '\\hat{I}', description: 'I-hat', category: 'Hat' },
    { trigger: ['Jhat', 'J_hat'], unicode: 'Ĵ', latex: '\\hat{J}', description: 'J-hat', category: 'Hat' },
    { trigger: ['Khat', 'K_hat'], unicode: 'K̂', latex: '\\hat{K}', description: 'K-hat', category: 'Hat' },
    { trigger: ['Nhat', 'N_hat'], unicode: 'N̂', latex: '\\hat{N}', description: 'N-hat', category: 'Hat' },
    { trigger: ['Phat', 'P_hat'], unicode: 'P̂', latex: '\\hat{P}', description: 'P-hat', category: 'Hat' },
    { trigger: ['Qhat', 'Q_hat'], unicode: 'Q̂', latex: '\\hat{Q}', description: 'Q-hat', category: 'Hat' },
    { trigger: ['Xhat', 'X_hat'], unicode: 'X̂', latex: '\\hat{X}', description: 'X-hat', category: 'Hat' },
    { trigger: ['Yhat', 'Y_hat'], unicode: 'Ŷ', latex: '\\hat{Y}', description: 'Y-hat', category: 'Hat' },
    { trigger: ['Zhat', 'Z_hat'], unicode: 'Ẑ', latex: '\\hat{Z}', description: 'Z-hat', category: 'Hat' },

    // ==================================================================================
    // TILDE — approximation, normalized variables, Fourier transform
    // ==================================================================================
    { trigger: ['atilde', 'a_tilde'], unicode: 'ã', latex: '\\tilde{a}', description: 'a-tilde', category: 'Tilde' },
    { trigger: ['btilde', 'b_tilde'], unicode: 'b̃', latex: '\\tilde{b}', description: 'b-tilde', category: 'Tilde' },
    { trigger: ['ctilde', 'c_tilde'], unicode: 'c̃', latex: '\\tilde{c}', description: 'c-tilde', category: 'Tilde' },
    { trigger: ['ftilde', 'f_tilde'], unicode: 'f̃', latex: '\\tilde{f}', description: 'f-tilde', category: 'Tilde' },
    { trigger: ['gtilde', 'g_tilde'], unicode: 'g̃', latex: '\\tilde{g}', description: 'g-tilde', category: 'Tilde' },
    { trigger: ['htilde', 'h_tilde'], unicode: 'h̃', latex: '\\tilde{h}', description: 'h-tilde', category: 'Tilde' },
    { trigger: ['mtilde', 'm_tilde'], unicode: 'm̃', latex: '\\tilde{m}', description: 'm-tilde', category: 'Tilde' },
    { trigger: ['ntilde', 'n_tilde'], unicode: 'ñ', latex: '\\tilde{n}', description: 'n-tilde', category: 'Tilde' },
    { trigger: ['ptilde', 'p_tilde'], unicode: 'p̃', latex: '\\tilde{p}', description: 'p-tilde', category: 'Tilde' },
    { trigger: ['qtilde', 'q_tilde'], unicode: 'q̃', latex: '\\tilde{q}', description: 'q-tilde', category: 'Tilde' },
    { trigger: ['rtilde', 'r_tilde'], unicode: 'r̃', latex: '\\tilde{r}', description: 'r-tilde', category: 'Tilde' },
    { trigger: ['stilde', 's_tilde'], unicode: 's̃', latex: '\\tilde{s}', description: 's-tilde', category: 'Tilde' },
    { trigger: ['ttilde', 't_tilde'], unicode: 't̃', latex: '\\tilde{t}', description: 't-tilde', category: 'Tilde' },
    { trigger: ['utilde', 'u_tilde'], unicode: 'ũ', latex: '\\tilde{u}', description: 'u-tilde', category: 'Tilde' },
    { trigger: ['vtilde', 'v_tilde'], unicode: 'ṽ', latex: '\\tilde{v}', description: 'v-tilde', category: 'Tilde' },
    { trigger: ['wtilde', 'w_tilde'], unicode: 'w̃', latex: '\\tilde{w}', description: 'w-tilde', category: 'Tilde' },
    { trigger: ['xtilde', 'x_tilde'], unicode: 'x̃', latex: '\\tilde{x}', description: 'x-tilde', category: 'Tilde', isPopular: true },
    { trigger: ['ytilde', 'y_tilde'], unicode: 'ỹ', latex: '\\tilde{y}', description: 'y-tilde', category: 'Tilde', isPopular: true },
    { trigger: ['ztilde', 'z_tilde'], unicode: 'z̃', latex: '\\tilde{z}', description: 'z-tilde', category: 'Tilde' },
    { trigger: ['Atilde', 'A_tilde'], unicode: 'Ã', latex: '\\tilde{A}', description: 'A-tilde', category: 'Tilde' },
    { trigger: ['Btilde', 'B_tilde'], unicode: 'B̃', latex: '\\tilde{B}', description: 'B-tilde', category: 'Tilde' },
    { trigger: ['Ctilde', 'C_tilde'], unicode: 'C̃', latex: '\\tilde{C}', description: 'C-tilde', category: 'Tilde' },
    { trigger: ['Ntilde', 'N_tilde'], unicode: 'Ñ', latex: '\\tilde{N}', description: 'N-tilde', category: 'Tilde' },
    { trigger: ['Xtilde', 'X_tilde'], unicode: 'X̃', latex: '\\tilde{X}', description: 'X-tilde', category: 'Tilde' },
    { trigger: ['Ytilde', 'Y_tilde'], unicode: 'Ỹ', latex: '\\tilde{Y}', description: 'Y-tilde', category: 'Tilde' },
    { trigger: ['Ztilde', 'Z_tilde'], unicode: 'Z̃', latex: '\\tilde{Z}', description: 'Z-tilde', category: 'Tilde' },
];

// ============= LocalStorage management =============
const STORAGE_KEY = 'study-board-custom-math-symbols';
const PATTERNS_STORAGE_KEY = 'study-board-custom-math-patterns';
const DISABLED_DEFAULTS_KEY = 'study-board-disabled-default-patterns-v2';

export function getCustomSymbols(): MathSymbol[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Failed to load custom symbols:', e);
        return [];
    }
}

function saveCustomSymbols(symbols: MathSymbol[]) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(symbols));
    } catch (e) {
        console.error('Failed to save custom symbols:', e);
    }
}

// ============= Firebase Sync for Patterns =============

interface FirebaseMathPatternSettings {
    customPatterns: MathPattern[];
    disabledDefaultPatterns: string[];
    lastModified: number;
}

// Load patterns from Firebase
export async function loadPatternsFromFirebase(userId: string): Promise<FirebaseMathPatternSettings | null> {
    if (!userId || userId === 'guest') return null;

    try {
        const settingsRef = doc(db, 'users', userId, 'settings', 'mathPatterns');
        const snapshot = await getDoc(settingsRef);

        if (snapshot.exists()) {
            return snapshot.data() as FirebaseMathPatternSettings;
        }
        return null;
    } catch (e) {
        console.error('Failed to load patterns from Firebase:', e);
        return null;
    }
}

// Save patterns to Firebase
export async function savePatternsToFirebase(userId: string, data: FirebaseMathPatternSettings): Promise<boolean> {
    if (!userId || userId === 'guest') return false;

    try {
        const settingsRef = doc(db, 'users', userId, 'settings', 'mathPatterns');
        await setDoc(settingsRef, {
            ...data,
            lastModified: Date.now()
        });
        return true;
    } catch (e) {
        console.error('Failed to save patterns to Firebase:', e);
        return false;
    }
}

// Refresh localStorage from Firebase (pull latest changes from other devices)
export async function refreshPatternsFromFirebase(userId: string): Promise<boolean> {
    if (!userId || userId === 'guest') return false;

    try {
        console.log('[MATH_SYNC] Refreshing patterns from Firebase...');
        const firebaseData = await loadPatternsFromFirebase(userId);

        if (firebaseData) {
            // Update localStorage with Firebase data
            localStorage.setItem(PATTERNS_STORAGE_KEY, JSON.stringify(firebaseData.customPatterns));
            localStorage.setItem(DISABLED_DEFAULTS_KEY, JSON.stringify(firebaseData.disabledDefaultPatterns));
            console.log('[MATH_SYNC] ✅ Refreshed from Firebase successfully');
            return true;
        }

        console.log('[MATH_SYNC] No Firebase data to refresh');
        return false;
    } catch (e) {
        console.error('[MATH_SYNC] ❌ Failed to refresh from Firebase:', e);
        return false;
    }
}

// Migrate localStorage → Firebase (one-time)
export async function migrateToFirebase(userId: string): Promise<boolean> {
    if (!userId || userId === 'guest') return false;

    try {
        // Check if Firebase already has data
        const existingData = await loadPatternsFromFirebase(userId);
        if (existingData) return true; // Already migrated

        // Get localStorage data
        const customPatterns = getCustomPatterns();
        const disabledDefaults = getDisabledDefaultPatterns();

        // Only migrate if there's data
        if (customPatterns.length > 0 || disabledDefaults.length > 0) {
            const success = await savePatternsToFirebase(userId, {
                customPatterns,
                disabledDefaultPatterns: disabledDefaults,
                lastModified: Date.now()
            });

            if (success) {
                console.log('✅ Math patterns migrated to Firebase');
            }
            return success;
        }

        return true;
    } catch (e) {
        console.error('Migration failed:', e);
        return false;
    }
}

// Get all symbols (default + custom)
export function getAllSymbols(): MathSymbol[] {
    return [...DEFAULT_SYMBOLS, ...getCustomSymbols()];
}

// CRUD operations
export function addCustomSymbol(symbol: Omit<MathSymbol, 'id' | 'isCustom'>): MathSymbol {
    const customSymbols = getCustomSymbols();
    const newSymbol: MathSymbol = {
        ...symbol,
        id: crypto.randomUUID(),
        isCustom: true,
    };
    customSymbols.push(newSymbol);
    saveCustomSymbols(customSymbols);
    return newSymbol;
}

export function updateCustomSymbol(id: string, updates: Partial<MathSymbol>): boolean {
    const customSymbols = getCustomSymbols();
    const index = customSymbols.findIndex(s => s.id === id);
    if (index === -1) return false;

    customSymbols[index] = { ...customSymbols[index], ...updates };
    saveCustomSymbols(customSymbols);
    return true;
}

export function deleteCustomSymbol(id: string): boolean {
    const customSymbols = getCustomSymbols();
    const filtered = customSymbols.filter(s => s.id !== id);
    if (filtered.length === customSymbols.length) return false;

    saveCustomSymbols(filtered);
    return true;
}

// ============= Symbol caching and search =============
let cachedAllSymbols: MathSymbol[] = [];
let cachedTriggerMap = new Map<string, MathSymbol>();
let cachedUnicodeToLatex = new Map<string, string>();
let cachedUnicodeToLatexAutoConvert = new Map<string, string>();
let cachedUnicodeSymbols = new Set<string>();

export function refreshSymbolCache(): void {
    cachedAllSymbols = getAllSymbols();

    cachedTriggerMap.clear();
    cachedAllSymbols.forEach(symbol => {
        symbol.trigger.forEach(trigger => {
            cachedTriggerMap.set(trigger.toLowerCase(), symbol);
        });
    });

    cachedUnicodeToLatex.clear();
    cachedUnicodeToLatexAutoConvert.clear();
    cachedAllSymbols.forEach(symbol => {
        cachedUnicodeToLatex.set(symbol.unicode, symbol.latex);
        // A one-character accented letter (ê, â, ã, ñ…) is also a letter of real words — Vietnamese
        // above all — so wrapping it in math would break "Tiêu đề" into "Ti$\hat{e}$u đề".
        const isWordLetter = [...symbol.unicode].length === 1 && /^[A-Za-z]\p{M}+$/u.test(symbol.unicode.normalize('NFD'));
        // Plain keyboard characters (<, >) are ordinary text too; only the true symbols get wrapped.
        const isPlainAscii = /^[\x20-\x7e]+$/.test(symbol.unicode);
        if (!symbol.skipAutoConvert && !isWordLetter && !isPlainAscii) {
            cachedUnicodeToLatexAutoConvert.set(symbol.unicode, symbol.latex);
        }
    });

    cachedUnicodeSymbols = new Set(cachedAllSymbols.map(s => s.unicode));
}

// Initialize cache
refreshSymbolCache();

// Export cached versions
export const TRIGGER_MAP = cachedTriggerMap;
export const UNICODE_TO_LATEX = cachedUnicodeToLatex;
// Subset of UNICODE_TO_LATEX excluding symbols whose Unicode form already renders correctly
// (skipAutoConvert: true). Used by parseMarkdown to avoid wrapping them in $...$ unnecessarily.
export const UNICODE_TO_LATEX_AUTOCONVERT = cachedUnicodeToLatexAutoConvert;
export const UNICODE_MATH_SYMBOLS = cachedUnicodeSymbols;

// Search for matching symbols based on partial input
export function searchMathSymbols(query: string, usages?: Record<string, number>): MathSymbol[] {
    if (!query) return [];
    const lowerQuery = query.toLowerCase();

    const results = cachedAllSymbols.filter(symbol =>
        symbol.trigger.some(t => t.toLowerCase().includes(lowerQuery)) ||
        symbol.description.toLowerCase().includes(lowerQuery) ||
        symbol.category.toLowerCase().includes(lowerQuery)
    );

    if (!usages || Object.keys(usages).length === 0) return results;

    return [...results].sort((a, b) => {
        const ka = a.id || a.latex;
        const kb = b.id || b.latex;
        return (usages[kb] || 0) - (usages[ka] || 0);
    });
}

// ============= Pattern System =============

const DEFAULT_PATTERNS: MathPattern[] = [
    // Vector/modifier notation
    { pattern: '{x}_vec', latex: '\\vec{{x}}', description: 'Vector notation', priority: 10, example: 'Velocity v_vec = m·a_vec by Newton\'s law' },
    { pattern: '{x}_hat', latex: '\\hat{{x}}', description: 'Hat notation', priority: 10, example: 'Unit normal n_hat points outward from the surface' },
    { pattern: '{x}_bar', latex: '\\bar{{x}}', description: 'Bar notation', priority: 10, example: 'Sample mean x_bar = (x_1 + ... + x_n) / n' },
    { pattern: '{x}_tilde', latex: '\\tilde{{x}}', description: 'Tilde notation', priority: 10, example: 'Approximation f_tilde(x) ≈ f(x) near operating point' },
    { pattern: '{x}_dot', latex: '\\dot{{x}}', description: 'Dot notation (derivative)', priority: 10, example: 'Velocity x_dot = dx/dt (Newton notation)' },
    { pattern: '{x}_ddot', latex: '\\ddot{{x}}', description: 'Double dot (2nd derivative)', priority: 10, example: 'Acceleration x_ddot = d²x/dt² by Newton\'s law' },

    // Matrix operations
    { pattern: '{A}T', latex: '{A}^\\top', description: 'Matrix transpose', priority: 15, mathZoneOnly: true, example: 'Symmetry condition: $AT$ = A means A is symmetric' },
    { pattern: '{A}inv', latex: '{A}^{-1}', description: 'Matrix inverse', priority: 15, example: 'Solve the system: x = Ainv · b when A is invertible' },
    { pattern: '{A}T{B}', latex: '{A}^\\top {B}', description: 'Transpose product', priority: 20, example: 'Gram matrix: ATB = ATB is always positive semi-definite' },
    { pattern: '{A}invT', latex: '{A}^{-\\top}', description: 'Inverse transpose', priority: 20, example: 'In optimization: gradient w.r.t. A involves AinvT' },

    // Subscripts and superscripts
    { pattern: '{x}_{i}', latex: '{x}_{{i}}', description: 'Subscript', priority: 5, example: 'The i-th component x_i of the feature vector x' },
    { pattern: '{x}^{n}', latex: '{x}^{{n}}', description: 'Superscript', priority: 5, example: 'Power rule: d/dx of x^n = n · x^(n-1)' },
    { pattern: '{x}({k})', latex: '{{x}}^{({{k}})}', description: 'Ordinal superscript', priority: 10, example: 'Iterate x(k) converges to x* as k → ∞' },

    // Fractions
    { pattern: '{A}/{B}', latex: '\\frac{{A}}{{B}}', description: 'Fraction', priority: 10, mathZoneOnly: true, example: 'Learning rate: $eta/n$ decreases as n grows' },
    { pattern: '∂{f}/∂{x}', latex: '\\frac{\\partial {{f}}}{\\partial {{x}}}', description: 'Partial derivative', priority: 15, example: 'At the optimum: ∂f/∂x = 0 and ∂f/∂y = 0' },

    // Big operators (Summation, Product, Integral)
    { pattern: 'Σ_{a}={b}:{c}', latex: '\\sum_{{a}={b}}^{{c}}', description: 'Summation (Greek)', priority: 15, example: 'Total loss = Σ_i=1:n of L(y_i, f(x_i))' },
    { pattern: '∑_{a}={b}:{c}', latex: '\\sum_{{a}={b}}^{{c}}', description: 'Summation (Operator)', priority: 15, example: 'Mean: x_bar = (1/n) · ∑_i=1:n of x_i' },
    { pattern: 'Π_{a}={b}:{c}', latex: '\\prod_{{a}={b}}^{{c}}', description: 'Product (Greek)', priority: 15, example: 'Joint probability = Π_k=1:n of P(x_k)' },
    { pattern: '∏_{a}={b}:{c}', latex: '\\prod_{{a}={b}}^{{c}}', description: 'Product (Operator)', priority: 15, example: 'Likelihood = ∏_i=1:n of p(x_i | θ)' },
    { pattern: '∫_{a}^{b}', latex: '\\int_{{a}}^{{b}}', description: 'Integral', priority: 15, example: 'Area under curve = ∫_0^1 of f(x) dx' },

    // Calculus & Analysis
    { pattern: 'lim_{x}->{a}', latex: '\\lim_{{x} \\to {a}}', description: 'Limit', priority: 15, example: 'Classic result: lim_x->0 of sin(x)/x = 1' },
    { pattern: '∇^{n}', latex: '\\nabla^{{n}}', description: 'Nabla power', priority: 10, example: 'Laplace equation: ∇^2 of f = 0 (harmonic function)' },

    // Norms and absolute values
    { pattern: '||{x}||', latex: '\\|{{x}}\\|', description: 'Norm', priority: 10, example: 'Euclidean distance: ||x - y|| = sqrt(∑ (x_i - y_i)^2)' },

    // Square root
    { pattern: 'sqrt({x})', latex: '\\sqrt{{x}}', description: 'Square root', priority: 10, example: 'Hypotenuse c = sqrt(a^2 + b^2) by Pythagoras' },

    // Statistics & Probability
    { pattern: 'E[{X}]', latex: '\\mathbb E[{{X}}]', description: 'Expectation', priority: 10, example: 'Bias: E[X_bar] = μ means the estimator is unbiased' },
    { pattern: 'Var({X})', latex: '\\operatorname{Var}({{X}})', description: 'Variance', priority: 10, example: 'Identity: Var(X) = E[X^2] - E[X]^2' },
    { pattern: 'P({A})', latex: 'P({{A}})', description: 'Probability', priority: 10, example: 'Complement rule: P(A) + P(Ac) = 1' },
    { pattern: 'P({A}|{B})', latex: 'P({{A}}|{{B}})', description: 'Conditional probability', priority: 15, example: 'Bayes theorem: P(A|B) = P(B|A) · P(A) / P(B)' },
    { pattern: 'C({n},{k})', latex: '\\binom{{n}}{{k}}', description: 'Binomial coefficient', priority: 10, example: 'Binomial: P(X=k) = C(n,k) · p^k · (1-p)^(n-k)' },

    // Optimization
    { pattern: 'argmin_{x}', latex: '\\arg\\min_{{x}}', description: 'Argmin', priority: 15, example: 'Least squares: w* = argmin_w of ||Xw - y||^2' },
    { pattern: 'argmax_{x}', latex: '\\arg\\max_{{x}}', description: 'Argmax', priority: 15, example: 'RL policy: π(s) = argmax_a of Q(s,a)' },
    { pattern: 'min_{x}', latex: '\\min_{{x}}', description: 'Minimum over x', priority: 15, example: 'Primal problem: min_x of f(x) subject to g(x) ≤ 0' },
    { pattern: 'max_{x}', latex: '\\max_{{x}}', description: 'Maximum over x', priority: 15, example: 'Dual problem: max_λ of the Lagrangian L(x,λ)' },
    { pattern: '{x}*', latex: '{{x}}^*', description: 'Optimal value', priority: 10, example: 'KKT condition: ∇f(x*) + λ·∇g(x*) = 0 at optimum x*' },

    // Linear Algebra
    { pattern: 'det({A})', latex: '\\det({{A}})', description: 'Determinant', priority: 10, example: 'A is invertible if and only if det(A) ≠ 0' },
    { pattern: 'tr({A})', latex: '\\operatorname{tr}({{A}})', description: 'Trace', priority: 10, example: 'Property: tr(A) = sum of all eigenvalues of A' },
    { pattern: 'rank({A})', latex: '\\operatorname{rank}({{A}})', description: 'Rank', priority: 10, example: 'Full rank: rank(A) = n means the columns are independent' },
    { pattern: '<{x},{y}>', latex: '\\langle {{x}},{{y}} \\rangle', description: 'Inner product', priority: 10, example: 'Angle: cos(θ) = <x,y> / (||x|| · ||y||)' },

    // Logic & Relations
    { pattern: '=>', latex: '\\Rightarrow', description: 'Implies', priority: 5, mathZoneOnly: true, example: 'If x > 0 then $x => ln(x)$ is defined' },
    { pattern: '<=>', latex: '\\Leftrightarrow', description: 'If and only if', priority: 5, mathZoneOnly: true, example: 'Equivalence: $||x|| = 0 <=> x = 0$' },
    { pattern: '~=', latex: '\\approx', description: 'Approximately', priority: 5, mathZoneOnly: true, example: 'For large n: $x_bar ~= μ$ by the law of large numbers' },
    { pattern: 'prop', latex: '\\propto', description: 'Proportional to', priority: 10, mathZoneOnly: true, example: 'Posterior: $P(θ|x) prop P(x|θ) · P(θ)$' },

    // Floor & Ceiling
    { pattern: 'floor({x})', latex: '\\lfloor {{x}} \\rfloor', description: 'Floor', priority: 10, example: 'Bits needed: floor(log_2(n)) + 1 for integer n' },
    { pattern: 'ceil({x})', latex: '\\lceil {{x}} \\rceil', description: 'Ceiling', priority: 10, example: 'Pages required: ceil(n / pageSize)' },

    // Math Fonts - Calligraphic (script style)
    { pattern: 'cal_{X}', latex: '\\mathcal{{X}}', description: 'Calligraphic font', priority: 10, example: 'Loss function cal_L(θ) is what we minimize during training' },

    // Math Fonts - Blackboard bold
    { pattern: 'bb_{X}', latex: '\\mathbb{{X}}', description: 'Blackboard bold', priority: 10, example: 'x ∈ bb_R means x is a real number; A ∈ bb_R^(n×n)' },

    // Vectors & Matrices (advanced)
    { pattern: '{x}_bold', latex: '\\mathbf{{x}}', description: 'Bold vector', priority: 10, example: 'Feature vector x_bold ∈ bb_R^n contains all inputs' },
    { pattern: 'I_{n}', latex: 'I_{{n}}', description: 'Identity matrix', priority: 10, example: 'For any square matrix A: A · I_n = I_n · A = A' },
    { pattern: '{A}⊗{B}', latex: '{{A}} \\otimes {{B}}', description: 'Kronecker product', priority: 10, example: 'Block structure: A⊗B has rows(A)·rows(B) rows' },
    { pattern: '{A}∘{B}', latex: '{{A}} \\circ {{B}}', description: 'Hadamard product', priority: 10, example: 'Element-wise: (A∘B)_ij = a_ij · b_ij' },

    // Calculus (advanced)
    { pattern: 'H_{f}', latex: 'H_{{f}}', description: 'Hessian matrix', priority: 10, example: 'Second-order condition: H_f is PSD at local minimum' },
    { pattern: 'J_{f}', latex: 'J_{{f}}', description: 'Jacobian matrix', priority: 10, example: 'Chain rule in vector form: J_f = J_g · J_h' },
    { pattern: 'div({F})', latex: '\\nabla \\cdot {{F}}', description: 'Divergence', priority: 10, example: 'Gauss theorem: ∯ F · dA = ∭ div(F) dV' },
    { pattern: 'curl({F})', latex: '\\nabla \\times {{F}}', description: 'Curl', priority: 10, example: 'Irrotational field: curl(F) = 0 everywhere' },
    { pattern: 'd/d{x}', latex: '\\frac{d}{d{{x}}}', description: 'Derivative operator', priority: 10, example: 'Fundamental: d/dx of sin(x) = cos(x)' },

    // Statistics (additional)
    { pattern: 'Cov({X},{Y})', latex: '\\operatorname{Cov}({{X}},{{Y}})', description: 'Covariance', priority: 10, example: 'Formula: Cov(X,Y) = E[X·Y] - E[X]·E[Y]' },
    { pattern: 'Corr({X},{Y})', latex: '\\operatorname{Corr}({{X}},{{Y}})', description: 'Correlation', priority: 10, example: 'Normalized: Corr(X,Y) = Cov(X,Y) / (σ_X · σ_Y)' },
    { pattern: 'N({μ},{σ})', latex: '\\mathcal{N}({{μ}},{{σ}}^2)', description: 'Normal distribution', priority: 10, example: 'IQ scores follow N(μ,σ) with mean 100 and std 15' },

    // Complexity notation
    { pattern: 'O({n})', latex: 'O({{n}})', description: 'Big O notation', priority: 10, example: 'Merge sort runs in O(n) · log(n) time' },
    { pattern: 'Θ({n})', latex: '\\Theta({{n}})', description: 'Theta notation', priority: 10, example: 'Θ(n) means both upper and lower bound are tight' },

    // Set theory
    { pattern: '{x}in{S}', latex: '{{x}} \\in {{S}}', description: 'Element of', priority: 10, mathZoneOnly: true, example: 'For all $xinS$: f(x) must satisfy the constraint' },
    { pattern: '{A}⊆{B}', latex: '{{A}} \\subseteq {{B}}', description: 'Subset', priority: 10, example: 'Double containment: A⊆B and B⊆A implies A = B' },
];

export function getCustomPatterns(): MathPattern[] {
    try {
        const stored = localStorage.getItem(PATTERNS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error('Failed to load custom patterns:', e);
        return [];
    }
}

// Track current user for auto-sync
let currentUserId: string | null = null;

// Set current user (call this when user logs in)
export function setCurrentUser(userId: string | null) {
    console.log('[MATH_SYNC] setCurrentUser called:', userId);
    currentUserId = userId;
}

function saveCustomPatterns(patterns: MathPattern[]) {
    try {
        localStorage.setItem(PATTERNS_STORAGE_KEY, JSON.stringify(patterns));
        // Auto-sync to Firebase if user is logged in
        if (currentUserId && currentUserId !== 'guest') {
            syncToFirebase();
        }
    } catch (e) {
        console.error('Failed to save custom patterns:', e);
    }
}

// Sync current localStorage state to Firebase (debounced)
let syncTimeout: NodeJS.Timeout | null = null;
async function syncToFirebase() {
    if (!currentUserId || currentUserId === 'guest') return;

    // Debounce: wait 1s after last change
    if (syncTimeout) clearTimeout(syncTimeout);

    syncTimeout = setTimeout(async () => {
        try {
            const customPatterns = getCustomPatterns();
            const disabledDefaults = getDisabledDefaultPatterns();

            await savePatternsToFirebase(currentUserId!, {
                customPatterns,
                disabledDefaultPatterns: disabledDefaults,
                lastModified: Date.now()
            });
        } catch (e) {
            console.error('Auto-sync to Firebase failed:', e);
        }
    }, 1000);
}

// Helper to get disabled default patterns
function getDisabledDefaultPatterns(): string[] {
    try {
        const stored = localStorage.getItem(DISABLED_DEFAULTS_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        return [];
    }
}

function normalizePattern(p: string): string {
    return p.trim();
}

export function getAllPatterns(): MathPattern[] {
    const customPatterns = getCustomPatterns();
    const disabledDefaults = getDisabledDefaultPatterns();

    const defaultPatterns = DEFAULT_PATTERNS.map(p => {
        // Strict check with normalization
        const isDisabled = disabledDefaults.some(d => normalizePattern(d) === normalizePattern(p.pattern));
        return { ...p, isActive: !isDisabled };
    });

    const allPatterns = [...defaultPatterns, ...customPatterns.map(p => ({ ...p, isActive: p.isActive !== false }))];
    // Sort by priority (higher first)
    return allPatterns.sort((a, b) => (b.priority || 0) - (a.priority || 0));
}

export function addCustomPattern(pattern: Omit<MathPattern, 'id' | 'isCustom'>): MathPattern {
    const customPatterns = getCustomPatterns();
    const newPattern: MathPattern = {
        ...pattern,
        id: crypto.randomUUID(),
        isCustom: true,
        isActive: true,
    };
    customPatterns.push(newPattern);
    saveCustomPatterns(customPatterns);
    return newPattern;
}

export function updateCustomPattern(id: string, updates: Partial<MathPattern>): boolean {
    const customPatterns = getCustomPatterns();
    const index = customPatterns.findIndex(p => p.id === id);
    if (index === -1) return false;

    customPatterns[index] = { ...customPatterns[index], ...updates };
    saveCustomPatterns(customPatterns);
    return true;
}

export function toggleCustomPattern(id: string): boolean {
    const customPatterns = getCustomPatterns();
    const index = customPatterns.findIndex(p => p.id === id);

    if (index !== -1) {
        // It's a custom pattern
        const currentStatus = customPatterns[index].isActive !== false;
        customPatterns[index] = { ...customPatterns[index], isActive: !currentStatus };
        saveCustomPatterns(customPatterns);
        return true;
    }

    return false;
}

export function togglePattern(patternStr: string, id?: string): boolean {
    // If ID is provided and found in custom patterns, toggle it
    if (id) {
        const result = toggleCustomPattern(id);
        if (result) return true;
    }

    // Otherwise, check if it matches a default pattern
    const normPattern = normalizePattern(patternStr);
    const defaultMatch = DEFAULT_PATTERNS.find(p => normalizePattern(p.pattern) === normPattern);

    if (defaultMatch) {
        const disabledDefaults = getDisabledDefaultPatterns();
        const isDisabled = disabledDefaults.some(d => normalizePattern(d) === normPattern);

        let newDisabled;
        if (isDisabled) {
            // Re-enable: remove from disabled list
            newDisabled = disabledDefaults.filter(p => normalizePattern(p) !== normPattern);
        } else {
            // Disable: add to disabled list
            newDisabled = [...disabledDefaults, normPattern];
        }

        localStorage.setItem(DISABLED_DEFAULTS_KEY, JSON.stringify(newDisabled));
        // Auto-sync to Firebase
        console.log('[MATH_SYNC] togglePattern - currentUserId:', currentUserId);
        if (currentUserId && currentUserId !== 'guest') {
            console.log('[MATH_SYNC] Triggering syncToFirebase()');
            syncToFirebase();
        } else {
            console.log('[MATH_SYNC] Skipping sync - user is', currentUserId || 'null');
        }
        return true;
    }

    return false;
}

export function disableAllPatterns(): void {
    // Disable all default patterns
    const allDefaultPatterns = DEFAULT_PATTERNS.map(p => normalizePattern(p.pattern));
    localStorage.setItem(DISABLED_DEFAULTS_KEY, JSON.stringify(allDefaultPatterns));
    // Auto-sync to Firebase
    if (currentUserId && currentUserId !== 'guest') {
        syncToFirebase();
    }

    // Disable all custom patterns
    const customPatterns = getCustomPatterns();
    const updated = customPatterns.map(p => ({ ...p, isActive: false }));
    saveCustomPatterns(updated);
}

export function enableAllPatterns(): void {
    // Enable all default patterns (clear disabled list)
    localStorage.setItem(DISABLED_DEFAULTS_KEY, JSON.stringify([]));
    // Auto-sync to Firebase
    if (currentUserId && currentUserId !== 'guest') {
        syncToFirebase();
    }

    // Enable all custom patterns
    const customPatterns = getCustomPatterns();
    const updated = customPatterns.map(p => ({ ...p, isActive: true }));
    saveCustomPatterns(updated);
}

export function deleteCustomPattern(id: string): boolean {
    const customPatterns = getCustomPatterns();
    const filtered = customPatterns.filter(p => p.id !== id);
    if (filtered.length === customPatterns.length) return false;

    saveCustomPatterns(filtered);
    return true;
}

// ============= Pattern Application =============

function getPlaceholders(pattern: string): string[] {
    const matches = pattern.matchAll(/\{([^}]+)\}/g);
    return Array.from(matches, m => m[1]);
}

export function patternToRegex(pattern: string): RegExp {
    let regexStr = '';
    let i = 0;

    while (i < pattern.length) {
        // Insert optional whitespace handler if not at start
        if (i > 0) {
            regexStr += '\\s*';
        }

        if (pattern[i] === '{' && i + 2 < pattern.length && pattern[i + 2] === '}') {
            // Found placeholder like {x}
            const isStart = (i === 0);
            const isEnd = (i + 3 === pattern.length);

            // Regex for parenthesized group: (...) - Limit length to 50 chars to prevent matching whole sentences
            const parensRegex = '\\([^)]{1,50}\\)';
            // Regex for single char: [a-zA-Z0-9]
            const charRegex = '[a-zA-Z0-9]';

            // Construct branch: ( parens | char )
            // No \b at end: allows matching a single-char variable that is part of a longer
            // token like `xi` (x subscript i), e.g. ∂L/∂xi should match with {x}=x, leaving `i`.
            const charBranch = (isStart ? '\\b' : '') + charRegex;

            regexStr += `(${parensRegex}|${charBranch})`;

            i += 3; // Skip {x}
        } else {
            // Handle Literals
            const char = pattern[i];
            const isWordChar = /[a-zA-Z0-9]/.test(char);

            // Add \b at start if literal is word char
            if (i === 0 && isWordChar) {
                regexStr += '\\b';
            }

            // Escape special regex characters
            if (/[.*+?^${}()|[\]\\]/.test(char)) {
                regexStr += '\\' + char;
                // Prevent * from matching ** (bold/italic markers)
                if (char === '*') regexStr += '(?!\\*)';
            } else {
                regexStr += char;
            }

            // Add \b at end if literal is word char and it's the last char
            if (i === pattern.length - 1 && isWordChar) {
                regexStr += '\\b';
            }

            i++;
        }
    }

    return new RegExp(regexStr, 'g');
}

export function applyPattern(text: string, pattern: MathPattern): string {
    const regex = patternToRegex(pattern.pattern);
    const placeholders = getPlaceholders(pattern.pattern);

    return text.replace(regex, (match, ...args) => {
        // Remove offset and string from args
        const captures = args.slice(0, -2);

        // Replace placeholders in LaTeX template
        let result = pattern.latex;
        captures.forEach((capture, index) => {
            if (index < placeholders.length) {
                const name = placeholders[index];

                // Clean the capture: remove existing $ delimiters to prevent nesting
                const cleanCapture = capture.replace(/\$/g, '');

                // Replace all occurrences of {name} pattern
                result = result.split(`{${name}}`).join(cleanCapture);
            }
        });

        // Auto-wrap with $ delimiters for KaTeX rendering
        return `$${result}$`;
    });
}

// Apply all patterns to text (skips mathZoneOnly patterns — those are handled by applyPatternsInMathZone)
export function applyAllPatterns(text: string): string {
    const patterns = getAllPatterns();
    let result = text;

    for (const pattern of patterns) {
        if (pattern.isActive !== false && !pattern.mathZoneOnly) {
            result = applyPattern(result, pattern);
        }
    }

    return result;
}

// Apply ALL patterns (including mathZoneOnly) inside a $$...$$ block.
// Returns raw LaTeX — NO $...$ wrapping, since the caller already wraps in $$.
export function applyPatternsInMathZone(content: string): string {
    const patterns = getAllPatterns();
    let result = content;

    for (const pattern of patterns) {
        if (pattern.isActive === false) continue;
        const regex = patternToRegex(pattern.pattern);
        const placeholders = getPlaceholders(pattern.pattern);
        result = result.replace(regex, (_match, ...args) => {
            const captures = args.slice(0, -2);
            let latex = pattern.latex;
            captures.forEach((capture, index) => {
                if (index < placeholders.length) {
                    const name = placeholders[index];
                    const cleanCapture = capture.replace(/\$/g, '');
                    latex = latex.split(`{${name}}`).join(cleanCapture);
                }
            });
            return latex; // raw LaTeX, no $...$ — already inside $$...$$
        });
    }

    return result;
}


// Per-notebook patterns on/off switch (mirrors setActiveNotebookAISettings pattern)
let _notebookPatternsEnabled = true;
export const setNotebookPatternsEnabled = (enabled: boolean) => { _notebookPatternsEnabled = enabled; };
export const isNotebookPatternsEnabled = () => _notebookPatternsEnabled;
